import React, { FC, useEffect, useState } from "react";
import { LanguageModel, TranscriptModel } from "../../../core/_models";
import Transcript from "./Transcript";
import { useThemeMode } from "../../../../../../_metronic/partials";
import { SuspensedView } from "../../../../../routing/PrivateRoutes";
import HighlightSelect from "./components/HighlightSelect";
import HighlightPopover from "../../../../../../_metronic/partials/popover/HighlightPopover";

interface TranscriptWrapperProps {
  playerTime: React.MutableRefObject<number>;
  seekedTime: number;
  updateTags: boolean;
  onWordClick: (time: number) => void;
  onFailedToFetchTranscript: () => void;
  width: number;
  eventId: string;
  availableLanguages?: LanguageModel[];
  embedded?: boolean;
  hasTranscript?: boolean;
}

const TranscriptWrapper: FC<TranscriptWrapperProps> = ({
  playerTime,
  seekedTime,
  onWordClick,
  updateTags,
  width,
  eventId,
  embedded,
  availableLanguages,
  hasTranscript,
  onFailedToFetchTranscript,
}) => {
  const { customBtnColor, customTextColor } = useThemeMode();
  const [transcript, setTranscript] = useState<TranscriptModel | undefined>(
    {} as TranscriptModel
  );
  const [shouldApplySentimentAnalysis, setShouldApplySentimentAnalysis] =
    useState<boolean>(false);
  const [language, setLanguage] = useState<LanguageModel | undefined>(
    undefined
  );
  const [displayTags, setDisplayTags] = useState<boolean>(false);

  useEffect(() => {
    if (hasTranscript) {
      if (availableLanguages && availableLanguages.length > 0) {
        setLanguage(availableLanguages[0]);
      }
    }
  }, [hasTranscript]);

  useEffect(() => {
    if (transcript && transcript.utterances)
      onWordClick(transcript?.utterances[0].start);
  }, [transcript]);

  const [highlightTags, setHighlightTags] = useState<boolean>(false);

  return (
    <div
      className={`${
        width < 576
          ? "transcript-height-mobile"
          : "transcript-height card card custom"
      } ${embedded ? "rounded-0" : ""}`}
      style={{ height: embedded ? "calc(100vh - 100px)" : "" }}
    >
      {embedded ? (
        <></>
      ) : (
        <div className="card-header">
          {width < 576 ? (
            <></>
          ) : (
            <h1 className="card-title custom-title">Event transcript</h1>
          )}

          <div
            className={`card-toolbar d-flex align-items-center ${
              embedded ? "pt-3 ps-5" : ""
            }`}
          >
            {((transcript && transcript.sentiment_analysis) ||
              (transcript && transcript.tagged_content)) && (
              <>
                <HighlightPopover
                  hasSentiment={
                    transcript && transcript.sentiment_analysis ? true : false
                  }
                  hasTags={
                    transcript && transcript.tagged_content ? true : false
                  }
                  position="bottom"
                ></HighlightPopover>
                <HighlightSelect
                  onDisplayTags={setDisplayTags}
                  onHighlightSentiment={setShouldApplySentimentAnalysis}
                  onHighlightTags={setHighlightTags}
                  hasSentiment={
                    transcript && transcript.sentiment_analysis ? true : false
                  }
                  hasTags={
                    transcript && transcript.tagged_content ? true : false
                  }
                ></HighlightSelect>
              </>
            )}
          </div>
        </div>
      )}
      <div
        className={`card-body ${
          embedded ? "pt-1" : "pt-5"
        } card-body-height p-0`}
      >
        {((transcript && transcript.sentiment_analysis) ||
          (transcript && transcript.tagged_content)) &&
          embedded && (
            <div
              className="d-flex w-100 ms-3 me-5 mb-1 align-items-center"
              style={{ height: "min-content" }}
            >
              <HighlightPopover
                hasSentiment={
                  transcript && transcript.sentiment_analysis ? true : false
                }
                embedded={embedded}
                hasTags={transcript && transcript.tagged_content ? true : false}
                position="bottom"
              ></HighlightPopover>
              <HighlightSelect
                onDisplayTags={setDisplayTags}
                onHighlightSentiment={setShouldApplySentimentAnalysis}
                onHighlightTags={setHighlightTags}
                hasSentiment={
                  transcript && transcript.sentiment_analysis ? true : false
                }
                hasTags={transcript && transcript.tagged_content ? true : false}
              ></HighlightSelect>
            </div>
          )}

        <div
          className={`transcript-content-wrapper w-100 d-flex ${
            width > 576 ? "flex-row" : "flex-column"
          } ${embedded ? "content-box-height-embedded" : "content-box-height"}`}
        >
          <div className="d-flex flex-column h-100 w-100">
            <div className="d-flex ms-5 language-bar">
              {availableLanguages &&
                availableLanguages.length > 1 &&
                language &&
                availableLanguages.map((availableLanguage) => (
                  <a
                    key={availableLanguage.code}
                    onClick={() => setLanguage(availableLanguage)}
                    className={`btn btn-sm btn-ribbon-lang ${
                      language.code == availableLanguage.code
                        ? customBtnColor
                          ? "btn-iframe-custom " + customTextColor
                          : "btn-ribbon-lang-active btn-primary"
                        : customBtnColor
                        ? "btn-iframe-custom-darker " + customTextColor
                        : "btn-light-primary btn-active-light-primary"
                    }`}
                    style={
                      customBtnColor
                        ? { "--custom-btn-color": customBtnColor }
                        : {}
                    }
                  >
                    {availableLanguage.name}
                  </a>
                ))}
            </div>
            <SuspensedView
              additionalFallback={
                <TranscriptFallback
                  width={width}
                  embedded={embedded}
                ></TranscriptFallback>
              }
            >
              <Transcript
                embedded={embedded}
                availableLanguages={availableLanguages}
                onFailedToFetchTranscript={onFailedToFetchTranscript}
                highlightTags={highlightTags}
                updateTags={updateTags}
                transcriptLanguage={language}
                displayTags={displayTags}
                onTranscript={(transcript) => setTranscript(transcript)}
                onWordClick={onWordClick}
                eventId={eventId}
                hasTranslations={
                  language == undefined || language == null
                    ? false
                    : language.code == null
                    ? false
                    : true
                }
                seekedTime={seekedTime}
                playerTime={playerTime}
                shouldApplySentimentAnalysis={shouldApplySentimentAnalysis}
              ></Transcript>
            </SuspensedView>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranscriptWrapper;

export interface TranscriptFallbackProps {
  width: number;
  embedded?: boolean;
}

export const TranscriptFallback: React.FC<TranscriptFallbackProps> = ({
  width,
  embedded,
}) => {
  return (
    <div
      className={`scrollable ${
        embedded ? "" : "w-100 text-box-height"
      } d-flex flex-column align-center justify-content-center ${
        embedded ? "rounded-0" : ""
      }`}
      style={{ height: embedded ? "100%" : "", width: "calc(100vw - 5px)" }}
    >
      <p className="font-weight-bold mt-4 align-self-center text-muted fs-5">
        <i className="ki-outline ki-document fs-1 align-self-center mb-0 me-2"></i>
        Loading the transcript...
      </p>
      <span className="spinner-border spinner-border-large align-self-center text-muted"></span>
    </div>
  );
};
